import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserSessionPersistence, inMemoryPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';

const useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Use the current hostname as authDomain when on a custom/production domain.
// Firebase Hosting serves /__/auth/handler on all connected domains, so the
// popup/redirect stays same-origin and avoids cross-origin postMessage failures.
const resolveAuthDomain = (): string => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  }
  return hostname;
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;

// Only initialize Firebase if not using mock data and config is available
if (!useMockData && firebaseConfig.apiKey && firebaseConfig.projectId) {
  // Initialize Firebase app (only if not already initialized)
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }
  
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  
  // Configure persistence for redirect-based auth in incognito mode
  // CRITICAL: Session persistence (sessionStorage) is more reliable in incognito mode than IndexedDB
  const configurePersistence = async () => {
    if (auth) {
      try {
        // Use session persistence (sessionStorage) - works reliably in incognito mode
        // This persists auth state for the browser tab session, including across redirects
        await setPersistence(auth, browserSessionPersistence);
        console.log('[AUTH] Session persistence configured successfully');
      } catch (error) {
        console.error('Session persistence failed:', error);
        // If session storage also fails, the redirect won't work properly
        // but we'll let it try anyway
      }
    }
  };
  
  configurePersistence();
}

export { app, auth, db, storage, useMockData };
export const isFirebaseEnabled = () => !useMockData && !!app && !!auth && !!db;
